import { useCallback, useEffect, useRef, useState } from "react";
import { confirmUpload, requestUpload, uploadToStorage } from "api/files";
import type { ConfirmedFile, FileUploadStatus } from "types";

type UploadState = {
  status: FileUploadStatus;
  progress: number;
  uploadedFile: ConfirmedFile | null;
  error: string | null;
};

const INITIAL_STATE: UploadState = {
  status: "idle",
  progress: 0,
  uploadedFile: null,
  error: null,
};

export default function useFileUpload() {
  const [state, setState] = useState<UploadState>(INITIAL_STATE);
  const attemptRef = useRef<symbol | null>(null);

  useEffect(() => () => {
    attemptRef.current = null;
  }, []);

  const reset = useCallback(() => {
    attemptRef.current = null;
    setState(INITIAL_STATE);
  }, []);

  async function upload(file: File): Promise<ConfirmedFile | null> {
    const attempt = Symbol();
    attemptRef.current = attempt;
    const isActive = () => attemptRef.current === attempt;
    setState({ ...INITIAL_STATE, status: "requesting" });

    try {
      const permission = await requestUpload(file);
      if (!isActive()) return null;

      setState((current) => ({ ...current, status: "uploading" }));
      await uploadToStorage(file, permission.upload, (progress) => {
        if (isActive()) setState((current) => ({ ...current, progress }));
      });
      if (!isActive()) return null;

      setState((current) => ({ ...current, status: "confirming", progress: 100 }));
      const uploadedFile = await confirmUpload(permission.fileId);
      if (!isActive()) return null;

      setState({ status: "ready", progress: 100, uploadedFile, error: null });
      return uploadedFile;
    } catch (error) {
      if (isActive()) {
        setState((current) => ({
          ...current,
          status: "error",
          uploadedFile: null,
          error: error instanceof Error ? error.message : "No se pudo completar la carga del archivo.",
        }));
      }
      return null;
    } finally {
      if (attemptRef.current === attempt) attemptRef.current = null;
    }
  }

  return { ...state, upload, reset };
}
